import {
  createRouter,
  createWebHashHistory,
  RouteLocationNormalized,
  Router,
  RouteRecordRaw,
} from "vue-router";
import {
  EntityDefinitionDto,
  SysOrgModule,
  SysOrgRoleName,
  WorkflowDefinitionDto,
} from "~/api";
import { getEnvConfig } from "~/services/appConfig";
import { authStore } from "~/stores/authStore";
import { useMyInfoStore as useComplianceMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { AuthStoreType } from "~/types/auth";
import {
  initializeActivityTracking,
  trackPageView,
} from "~/utils/activityTracker";
import { useShowPortalLoginButton } from "~/utils/showPortalLoginButton";
const showPortalLoginButton = useShowPortalLoginButton();

// YYC Housing routes (extracted to housing module)
import {
  housingRouteGuard,
  housingRoutes,
} from "~/YYC_Housing/router/housingRouter";

// Compliance imports
import ItemDefinitionEditor from "~/Compliance/components/ItemDefinitionEditor.vue";
import OrgGroupList from "~/Compliance/components/OrgGroupList.vue";
import AllowedSaveToTableList from "~/Compliance/pages/AllowedSaveToTableList.vue";
import ChecklistDefinitionList from "~/Compliance/pages/ChecklistDefinitionList.vue";
import ContactListingPage from "~/Compliance/pages/ContactListingPage.vue";
import CRMEntityDetail from "~/Compliance/pages/CRMEntityDetail.vue";
import CRMEntityList from "~/Compliance/pages/CRMEntityList.vue";
import EmailStyleTemplateList from "~/Compliance/pages/EmailStyleTemplateList.vue";
import EmailTemplateList from "~/Compliance/pages/EmailTemplateList.vue";
import EntityDefinitionList from "~/Compliance/pages/EntityDefinitionList.vue";
import EntityList from "~/Compliance/pages/EntityList.vue";
import FormResponseTemplate from "~/Compliance/pages/FormResponseTemplate.vue";
import ItemDefinitionList from "~/Compliance/pages/ItemDefinitionList.vue";
import MainDashboard from "~/Compliance/pages/MainDashboard.vue";
import HelpFaq from "~/pages/HelpFaq.vue";
import OrgList from "~/Compliance/pages/OrgList.vue";
import RawForms from "~/Compliance/pages/RawForms.vue";
import Start from "~/Compliance/pages/Start.vue";
import SupportTickets from "~/pages/SupportTickets.vue";
import TreeDefinitionList from "~/Compliance/pages/TreeDefinitionList.vue";
import WorkflowDefinitionList from "~/Compliance/pages/WorkflowDefinitionList.vue";
import WorkflowList from "~/Compliance/pages/WorkflowList.vue";
import RouterViewWrapper from "~/components/RouterViewWrapper.vue";
import WorkflowPdfViewer from "~/NewFormBuilder/pages/WorkflowPdfViewer.vue";
import ComplianceHomePage from "~/pages/HomePage.vue";
import LandingPage from "~/pages/LandingPage.vue";

// special routes
const unmatched = "/:pathMatch(.*)*";

type metaInfo = {
  anonymous?: boolean;
  HMB?: boolean;
  HAVN?: boolean;
  SeniorsSide?: boolean;
};

const anonymousMeta: metaInfo = { anonymous: true };

export const applicationRouteMap = {
  // Root redirect
  Root: {
    path: "/",
    redirect: () => {
      if (authStore.account) {
        return { path: "/dashboard" };
      }
      return { path: "/intake" };
    },
  },

  // Housing routes (Intake + HMB portal) - defined in YYC_Housing module
  ...housingRoutes,

  // Compliance routes
  Compliance: {
    path: "/compliance/",
    component: ComplianceHomePage,
    redirect: () => {
      if (authStore.account) {
        return { path: "/dashboard" };
      }
      return { path: "/portal/landing" };
    },
    children: {
      Login: {
        path: "landing",
        meta: {
          display: "Landing",
          anonymous: true,
        },
        component: LandingPage,
        name: "ComplianceLanding",
      },
      Dashboard: {
        path: "",
        name: "ComplianceDashboardWrapper",
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: {
          Main: {
            path: "main-dashboard",
            component: MainDashboard,
            name: "ComplianceDashboard",
            meta: {
              display: "Summaries",
              roles: [SysOrgRoleName.MEMBER],
            },
          },
          Start: {
            path: "start/:shortcode",
            meta: {
              display: "Start",
              anonymous: true,
            },
            component: Start,
            name: "ComplianceStart",
          },
        },
      },

      Workflows: {
        path: "",
        name: "WorkflowsWrapper",
        component: RouterViewWrapper,
        children: [
          {
            path: "view-workflows",
            name: "ViewWorkflows",
            meta: {
              display: "View",
              roles: [SysOrgRoleName.MEMBER, SysOrgRoleName.GUEST],
            },
            component: WorkflowList,
          },
        ],
      },

      ManageWorkflows: {
        path: "",
        name: "ManageWorkflows",
        component: RouterViewWrapper,
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: [] as RouteRecordRaw[],
      },

      Manage: {
        path: "",
        name: "Manage",
        component: RouterViewWrapper,
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: [] as RouteRecordRaw[],
      },

      CRM: {
        path: "",
        name: "CRM",
        component: RouterViewWrapper,
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: [] as RouteRecordRaw[],
      },

      CRMDetail: {
        path: "contact-management/:entityDefinitionGuid/:entityGuid",
        name: "CRMEntityDetail",
        meta: {
          display: "CRM Detail",
          roles: [SysOrgRoleName.MEMBER],
        },
        component: CRMEntityDetail,
      },

      Setup: {
        path: "",
        name: "SetupWrapper",
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: {
          EntityTypes: {
            path: "setup-entity-types",
            meta: {
              display: "Entities",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: EntityDefinitionList,
          },
          ComplianceItems: {
            path: "setup-items",
            meta: {
              display: "Items",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: ItemDefinitionList,
          },
          ComplianceChecklists: {
            path: "setup-checklists",
            meta: {
              display: "Checklists",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: ChecklistDefinitionList,
          },
          WorkflowDefinitionList: {
            path: "setup-workflows",
            meta: {
              display: "Workflows",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: WorkflowDefinitionList,
          },
          AllowedSaveToTables: {
            path: "setup-allowed-save-to-tables",
            meta: {
              display: "SaveToTable Fields",
              roles: [SysOrgRoleName.SETUP_BASIC],
            },
            component: AllowedSaveToTableList,
          },
          TreeDefinitions: {
            path: "setup-tree-definitions",
            meta: {
              display: "Tree Definitions",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: TreeDefinitionList,
          },
          HousingFormResponsesAudit: {
            path: "setup-housing-form-responses",
            meta: {
              display: "Housing Responses",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: () =>
              import("~/Compliance/pages/HousingFormResponsesAudit.vue"),
          },
        },
      },
      Email: {
        path: "",
        name: "EmailWrapper",
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: {
          EmailTemplateList: {
            path: "email-templates",
            meta: {
              display: "Templates",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: EmailTemplateList,
          },
          EmailStyleTemplateList: {
            path: "email-style-templates",
            meta: {
              display: "Styles",
              roles: [SysOrgRoleName.VIEW_SETUP],
            },
            component: EmailStyleTemplateList,
          },
        },
      },
      Admin: {
        path: "",
        name: "AdminWrapper",
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: {
          Organizations: {
            path: "organizations",
            meta: {
              display: "Organizations",
              roles: [SysOrgRoleName.USER_ADMIN],
              superAdmin: true,
            },
            component: OrgList,
          },
          Groups: {
            path: "groups",
            meta: {
              display: "Groups",
              roles: [SysOrgRoleName.USER_ADMIN],
            },
            component: OrgGroupList,
          },
          UserMembers: {
            path: "members",
            meta: {
              display: "Members",
              roles: [SysOrgRoleName.USER_ADMIN],
              whoToShow: SysOrgRoleName.MEMBER,
            },
            component: () => import("~/Compliance/pages/UserList.vue"),
          },
          UserGuests: {
            path: "guests",
            meta: {
              display: "Guests",
              roles: [SysOrgRoleName.USER_ADMIN],
              whoToShow: SysOrgRoleName.GUEST,
            },
            component: () => import("~/Compliance/pages/UserList.vue"),
          },
        },
      },

      TechSupport: {
        path: "",
        name: "TechSupportWrapper",
        redirect: () => {
          if (authStore.account) {
            return { path: "/compliance/main-dashboard" };
          }
          return { path: "/compliance/landing" };
        },
        children: {
          Forms: {
            path: "tech-forms",
            meta: {
              display: "Raw Forms",
              superAdmin: true,
            },
            component: RawForms,
          },
          FormPDFViewer: {
            path: "/pdf/:workflowGuid/:pdfCode?",
            meta: {
              display: "PDF Viewer",
              anonymous: true,
              skipActivityLog: true,
            },
            component: WorkflowPdfViewer,
          },
          CrossDbSync: {
            path: "cross-db-sync",
            meta: {
              display: "Cross DB Sync",
              superAdmin: true,
            },
            component: () => import("~/Compliance/pages/CrossDbSync.vue"),
          },
          OldHousingDataImport: {
            path: "old-housing-data-import",
            meta: {
              display: "Old Housing Data Import",
              superAdmin: true,
            },
            component: () =>
              import("~/Compliance/pages/OldHousingDataImport.vue"),
          },
          Scheduler: {
            path: "scheduler",
            meta: {
              display: "Scheduler",
              superAdmin: true,
            },
            component: () => import("~/Compliance/pages/SchedulerAdmin.vue"),
          },
          DataAnonymization: {
            path: "data-anonymization",
            meta: {
              display: "Data Anonymization",
              superAdmin: true,
            },
            component: () => import("~/Compliance/pages/DataAnonymization.vue"),
          },
        },
      },

      HelpAndSupport: {
        // Empty-path grouping wrapper, consistent with the other Compliance
        // sub-sections (Workflows, Manage, CRM, ...). It has no redirect of its
        // own — the Dashboard wrapper already owns the bare "/compliance"
        // redirect, so adding another here would be dead/ambiguous. Navigation
        // targets the children (/compliance/support-tickets, /compliance/help-faq).
        path: "",
        name: "HelpAndSupportWrapper",
        children: {
          SupportTickets: {
            path: "support-tickets",
            name: "ComplianceSupportTickets",
            meta: {
              display: "Support Tickets",
              roles: [SysOrgRoleName.MEMBER, SysOrgRoleName.GUEST],
            },
            component: SupportTickets,
          },
          HelpFaq: {
            path: "help-faq",
            name: "ComplianceHelpFaq",
            meta: {
              display: "Help / FAQ",
              roles: [SysOrgRoleName.MEMBER, SysOrgRoleName.GUEST],
            },
            component: HelpFaq,
          },
        },
      },

      ItemDesigner: {
        path: "compliance-item/:formGuid/:itemGuid/design",
        meta: {
          display: "Item Designer",
          roles: [SysOrgRoleName.SETUP_ITEMS],
          superAdmin: true,
        },
        component: ItemDefinitionEditor,
        props: true,
      },
      ComplianceFormRespond: {
        path: ":userGuid/forms/:formGuid/responses/:responseGuid",
        meta: {
          display: "Compliance Response",
          roles: [SysOrgRoleName.VIEW_SETUP],
        },
        component: FormResponseTemplate,
        props: true,
      },

      FormRespond: {
        path: "forms/:formGuid/responses/:responseGuid",
        meta: {
          display: "Form Response",
          roles: [SysOrgRoleName.VIEW_SETUP],
          superAdmin: true,
        },
        component: () => import("~/NewFormBuilder/pages/FormResponse.vue"),
        props: true,
      },

      FormDesigner: {
        path: "forms/:guid/design",
        meta: {
          display: "Form Designer",
          roles: [SysOrgRoleName.SETUP_BASIC],
          superAdmin: true,
        },
        component: () => import("~/NewFormBuilder/pages/FormDesignerPage.vue"),
        props: true,
      },
      FormResponses: {
        path: "forms/:guid/responses",
        meta: {
          display: "Form Responses",
          roles: [SysOrgRoleName.VIEW_SETUP],
          superAdmin: true,
        },
        component: () => import("~/NewFormBuilder/pages/FormResponsesList.vue"),
        props: true,
      },
    },
  },

  // special route to show the Login button for HMB users (mimic from YYC project)
  FinderHMBLoginAccess: {
    path: "/finder/hmb-login-access",
    name: "Finder portal access",
    redirect: () => {
      // give the portal login button to the user
      showPortalLoginButton.value = true;
      // and just redirect them to the default finder.
      // Now the portal login button is always shown
      return { path: "/finder" };
    },
  },

  NotFound: {
    path: unmatched,
    name: "NotFound",
    meta: anonymousMeta,
    component: () => import("~/pages/Client404.vue"),
  },
};

function mapRoute(route: RouteRecordRaw): RouteRecordRaw {
  return {
    ...route,
    children: route.children ? Object.values(route.children).map(mapRoute) : [],
  };
}

let routesAreReady = false;

export const workflowUrlPrefix = "workflows/";
export const crmUrlPrefix = "contact-management/";

const addMenuItemsForWorkflows = async () => {
  const orgInfoStore = useOrgInfoStore();
  await orgInfoStore.load();

  const workflowDefinitionList =
    orgInfoStore.MyOrgInfo?.workflowDefinitionDtos.filter(
      (wd) => !wd.entityDefinitionGuid,
    );
  if (workflowDefinitionList && workflowDefinitionList.length > 0) {
    workflowDefinitionList?.sort(
      (a: WorkflowDefinitionDto, b: WorkflowDefinitionDto) =>
        b.name.localeCompare(a.name),
    );

    workflowDefinitionList.forEach((dto1: WorkflowDefinitionDto) => {
      const menu1: RouteRecordRaw = {
        path: workflowUrlPrefix + dto1.guid.toLowerCase(),
        meta: {
          roles: [SysOrgRoleName.MEMBER, SysOrgRoleName.GUEST],
          workflowDefinitionGuid: dto1.guid,
          display: dto1.name,
        },
        component: WorkflowList,
        children: [],
      };

      router.addRoute("ManageWorkflows", menu1);
      applicationRouteMap.Compliance.children.ManageWorkflows.children.unshift(
        menu1,
      );
    });
    routesAreReady = true;
  }

  return workflowUrlPrefix;
};

const addMenuItemsForEntities = async () => {
  const orgInfoStore = useOrgInfoStore();
  await orgInfoStore.load();

  const prefix = "entity/";

  let entityList = orgInfoStore.MyOrgInfo?.entityDefinitionDtos;

  if (entityList && entityList.length > 0) {
    entityList?.sort((a: EntityDefinitionDto, b: EntityDefinitionDto) =>
      b.plural.localeCompare(a.plural),
    );

    const allEntities: EntityDefinitionDto[] = [];
    entityList.forEach((dto1: EntityDefinitionDto) => {
      if (dto1.childEntityDefinitions?.length) {
        dto1.childEntityDefinitions.forEach((child) => {
          allEntities.push(child);
        });
      }
      allEntities.push(dto1);
    });
    entityList = allEntities;

    entityList = entityList.filter(
      (dto) => dto.crmMenuOrder === null || dto.crmMenuOrder === undefined,
    );

    entityList?.forEach((dto1: EntityDefinitionDto) => {
      const menu1: RouteRecordRaw = {
        path: prefix + dto1.guid.toLowerCase(),
        meta: {
          roles: [SysOrgRoleName.MEMBER, SysOrgRoleName.GUEST],
          entityDefinitionGuid: dto1.guid,
          display: dto1.plural,
        },
        component: EntityList,
        children: [],
      };

      router.addRoute("Manage", menu1);
      applicationRouteMap.Compliance.children.Manage.children.unshift(menu1);
    });
    routesAreReady = true;
  }

  return prefix;
};

// Helper function to collect CRM entities recursively
const collectCrmEntities = (
  entities: EntityDefinitionDto[],
  result: EntityDefinitionDto[] = [],
) => {
  entities.forEach((entity) => {
    if (entity.crmMenuOrder !== null && entity.crmMenuOrder !== undefined) {
      result.push(entity);
    }
    if (entity.childEntityDefinitions?.length) {
      collectCrmEntities(entity.childEntityDefinitions, result);
    }
  });
  return result;
};

const addMenuItemsForCRM = async () => {
  const orgInfoStore = useOrgInfoStore();
  await orgInfoStore.load();

  const entityList = orgInfoStore.MyOrgInfo?.entityDefinitionDtos;

  if (entityList && entityList.length > 0) {
    const allCrmEntities = collectCrmEntities(entityList);

    if (allCrmEntities.length > 0) {
      allCrmEntities.sort(
        (a, b) => (a.crmMenuOrder ?? 0) - (b.crmMenuOrder ?? 0),
      );

      allCrmEntities.forEach((dto1: EntityDefinitionDto) => {
        const isContact = dto1.name === "Contact";

        const menu1: RouteRecordRaw = {
          path: crmUrlPrefix + dto1.guid.toLowerCase(),
          meta: {
            roles: [SysOrgRoleName.MEMBER],
            entityDefinitionGuid: dto1.guid,
            display: dto1.plural,
          },
          component: isContact ? ContactListingPage : CRMEntityList,
          children: [],
        };

        router.addRoute("CRM", menu1);
        applicationRouteMap.Compliance.children.CRM.children.push(menu1);
      });
    }
  }

  return crmUrlPrefix;
};

// create router
let router: Router;

function getMainRouter() {
  if (!router) {
    const envConfig = getEnvConfig();
    const basePath = new URL(envConfig.serverUrl).pathname;
    router = createRouter({
      history: createWebHashHistory(basePath),
      //@ts-expect-error: Recursive data type
      routes: Object.values(applicationRouteMap).map(mapRoute),
    });

    // hook MSAL into router

    router.beforeEach(async (to) => {
      let authStoreType: AuthStoreType | undefined = undefined;
      if (!authStore.initialized) {
        authStoreType = await authStore.setup(); // may still not be initialized if not yet logged in
      }

      const msalRedirect = await handleMsalRedirect(to, authStoreType);
      if (msalRedirect) {
        return msalRedirect;
      }

      if (to.path === "/dashboard") {
        return await handleDashboardRedirect();
      }

      if (to.meta.anonymous) {
        // Don't skip guard for catch-all routes under paths that use dynamic routes —
        // they need to proceed so dynamic routes can be loaded and retried.
        const mayNeedDynamicRoutes =
          to.name === "NotFound" && to.path.startsWith("/compliance");
        if (!mayNeedDynamicRoutes) {
          return true;
        }
      }

      const authResult = await ensureAuthenticated(to, authStoreType);
      if (authResult !== true) {
        return authResult;
      }

      const dynamicRouteRedirect = await loadDynamicRoutesIfNeeded(to);
      if (dynamicRouteRedirect) {
        return dynamicRouteRedirect;
      }

      //--> removed this, since it was forcing us the HAVN page even if we were going elsewhere
      // const havnAccess = await checkHavnRouteAccess(to);
      // if (havnAccess) {
      //   return havnAccess;
      // }

      // const complianceAccess = await checkComplianceRouteAccess(to);
      // if (complianceAccess === true) {
      //   return true;
      // }
      // if (complianceAccess) {
      //   return complianceAccess;
      // }

      const housingGuardResult = await housingRouteGuard(
        to,
        applicationRouteMap,
      );
      if (housingGuardResult !== undefined) {
        return housingGuardResult;
      }

      // console.log("Logged in, access granted to route:", to.fullPath);
      return true;
    });

    initializeActivityTracking();

    router.afterEach((to) => {
      trackPageView(to);
    });
  }
  return router;
}

// ---------------------------------------------------------------------------------------------------------------------
// authentication
// ---------------------------------------------------------------------------------------------------------------------

// hook MSAL into router

// set up auth and guard routes
const pageNotFound = (_path: string) => {
  // console.log("ROUTER: 404 - Page not found:", path);
  return { name: "NotFound" };
};

async function handleDashboardRedirect() {
  const myInfoStore = useComplianceMyInfoStore();
  await myInfoStore.load();

  // when in a Housing SysOrg
  if (myInfoStore.currentSysOrg?.modules?.includes(SysOrgModule.HOUSING)) {
    if (myInfoStore.isPortal) {
      return { path: "/portal/dashboard" };
    }
    return { path: "/application/home" };
  }
  // everyone else gets the generic dashboard
  return { path: "/compliance/main-dashboard" };
}

async function handleMsalRedirect(
  to: RouteLocationNormalized,
  authStoreType?: AuthStoreType,
) {
  const isMsalRedirect =
    to.fullPath.includes("state=") ||
    to.fullPath.includes("code=") ||
    to.fullPath.includes("client_info=");

  if (!isMsalRedirect) {
    return null;
  }

  if (authStore.isAuthenticated) {
    if (authStoreType === AuthStoreType.User) {
      return { name: "applicationHome" };
    }
    return await handleDashboardRedirect();
  }
  return { path: "/" };
}

async function ensureAuthenticated(
  to: RouteLocationNormalized,
  authStoreType?: AuthStoreType,
) {
  if (authStore.account) {
    return true;
  }

  // console.log("ROUTER: Not logged in, attempting login...");
  if (authStoreType) {
    await authStore.login(authStoreType);
  } else {
    const allMetas = to.matched.map((m) => m.meta as metaInfo);
    const hasHMBMeta = allMetas.some((m) => m.HMB);
    const hasHAVNMeta = allMetas.some((m) => m.HAVN);

    if (hasHMBMeta) {
      await authStore.login("hmb" as AuthStoreType);
    } else if (hasHAVNMeta) {
      await authStore.login(AuthStoreType.User);
    }
  }

  if (!authStore.account) {
    const allMetas = to.matched.map((m) => m.meta as metaInfo);
    const hasHMBMeta = allMetas.some((m) => m.HMB);
    const hasHAVNMeta = allMetas.some((m) => m.HAVN);

    if (!hasHMBMeta && !hasHAVNMeta) {
      return pageNotFound(to.fullPath);
    }
  }

  return true;
}

async function loadDynamicRoutesIfNeeded(to: RouteLocationNormalized) {
  if (!authStore.isAuthenticated) {
    return null;
  }

  if (!routesAreReady && to.path.startsWith("/compliance")) {
    const prefixes = [];
    prefixes.push(
      await addMenuItemsForEntities(),
      await addMenuItemsForWorkflows(),
      await addMenuItemsForCRM(),
    );

    if (to.name === "NotFound") {
      if (prefixes.some((prefix) => to.path.includes(prefix))) {
        return { path: to.path };
      }
    }
  }

  return null;
}

// async function checkHavnRouteAccess(to: RouteLocationNormalized) {
//   if (!to.path.startsWith("/portal")) {
//     return null;
//   }

//   const myInfoStore = useComplianceMyInfoStore();
//   await myInfoStore.load();

//   if (!myInfoStore.currentSysOrg?.modules?.includes(SysOrgModule.HOUSING)) {
//     // console.log("HAVN route access blocked - org does not have Housing module");
//     return { path: "/compliance/main-dashboard" };
//   }

//   if (to.meta.roles) {
//     const rolesRequired = to.matched
//       .map((m: any) => m.meta as { roles: SysOrgRoleName[] })
//       .reduce((acc: SysOrgRoleName[], m: any) => {
//         if (!m.roles?.length) {
//           return acc;
//         }
//         return [...acc, ...m.roles];
//       }, [] as SysOrgRoleName[]);

//     if (rolesRequired.length) {
//       if (
//         !rolesRequired.some((r: SysOrgRoleName) =>
//           myInfoStore.currentSysOrg?.roles.includes(r),
//         )
//       ) {
//         return pageNotFound(to.fullPath);
//       }
//     }
//   }

//   return null;
// }

// async function checkComplianceRouteAccess(to: RouteLocationNormalized) {
//   if (!to.path.startsWith("/compliance") || !to.meta.roles) {
//     return null;
//   }

//   const rolesRequired = to.matched
//     .map((m: any) => m.meta as { roles: SysOrgRoleName[] })
//     .reduce((acc: SysOrgRoleName[], m: any) => {
//       if (!m.roles?.length) {
//         return acc;
//       }
//       return [...acc, ...m.roles];
//     }, [] as SysOrgRoleName[]);

//   const allowSuperAdmin = to.matched.some((m: any) => m.meta.superAdmin);

//   const myInfoStore = useComplianceMyInfoStore();
//   await myInfoStore.load();

//   if (allowSuperAdmin && myInfoStore.isSuperAdmin) {
//     return true;
//   }

//   if (rolesRequired.length) {
//     if (
//       !rolesRequired.some((r: SysOrgRoleName) =>
//         myInfoStore.currentSysOrg?.roles.includes(r),
//       )
//     ) {
//       return pageNotFound(to.fullPath);
//     }
//   }

//   return null;
// }

// export
export default getMainRouter;
